/**
 * Game Feel Polish Phase 5 — the project had no frame-time or FPS
 * measurement of any kind (the DebugHud showed ping but never frame cost),
 * which meant no performance claim about this game could be honestly
 * verified. `design/systems/systems-index.md`'s own "WebGL scene budget at
 * target concurrency" risk lists its mitigation as "load-test ... with real
 * skinned-animation counts" — that requires an instrument, so this is it.
 *
 * A fixed-size ring buffer over the last `windowSize` frame deltas: no
 * allocation per frame (CLAUDE.md's no-per-frame-allocations rule for render
 * loops), and `worstMs` is kept because a mean alone hides exactly the
 * hitches that actually get noticed while playing.
 */
export class FrameTimer {
  private readonly samples: Float64Array;
  private index = 0;
  private count = 0;

  constructor(private readonly windowSize = 120) {
    this.samples = new Float64Array(windowSize);
  }

  push(deltaMs: number): void {
    this.samples[this.index] = deltaMs;
    this.index = (this.index + 1) % this.windowSize;
    if (this.count < this.windowSize) this.count++;
  }

  get meanMs(): number {
    if (this.count === 0) return 0;
    let total = 0;
    for (let i = 0; i < this.count; i++) total += this.samples[i];
    return total / this.count;
  }

  /** The slowest frame in the current window — a mean alone hides the hitches players actually feel. */
  get worstMs(): number {
    let worst = 0;
    for (let i = 0; i < this.count; i++) if (this.samples[i] > worst) worst = this.samples[i];
    return worst;
  }

  get fps(): number {
    const mean = this.meanMs;
    return mean > 0 ? 1000 / mean : 0;
  }
}
